import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';
import type { ComponentSize } from '@/types/general';
import { getComponentSize } from '@/types/general';

import { Icon } from '../ui/icon';

const spinnerVariants = cva(
  'flex animate-spin items-center justify-center',
  {
    variants: {
      variant: {
        // Default variant
        'default': 'text-primary',

        // Primary variants
        'primary': 'text-primary',
        'primary-light': 'text-primary-light',
        'primary-dark': 'text-primary-dark',

        // Secondary variants
        'secondary': 'text-secondary',
        'secondary-light': 'text-secondary-light',
        'secondary-dark': 'text-secondary-dark',

        // Info variants
        'info': 'text-info',
        'info-light': 'text-info-light',
        'info-dark': 'text-info-dark',

        // Success variants
        'success': 'text-success',
        'success-light': 'text-success-light',
        'success-dark': 'text-success-dark',

        // Warning variants
        'warning': 'text-warning',
        'warning-light': 'text-warning-light',
        'warning-dark': 'text-warning-dark',

        // Error variants
        'error': 'text-error',
        'error-light': 'text-error-light',
        'error-dark': 'text-error-dark',

        // Action variants
        'action': 'text-action',
        'action-selected': 'text-action-selected',

        // Inherit color from parent
        'inherit': 'text-inherit',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  },
);

export type SpinnerLoaderProps = {
  size?: ComponentSize;
} & React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof spinnerVariants>;

export default function SpinnerLoader({
  size = 'sm',
  variant,
  className,
  ...rest
}: SpinnerLoaderProps) {
  const pixelSize = getComponentSize(size);

  return (
    <div
      style={{ width: pixelSize, height: pixelSize }}
      className={cn('flex items-center justify-center', className)}
      {...rest}
    >
      <div className={cn(spinnerVariants({ variant }))}>
        <Icon
          icon="material-symbols:progress-activity"
          size={pixelSize}
        />
      </div>
    </div>
  );
}
